import { apiGet, apiPost, apiPut, apiDelete, planningPath } from './PlanningApiClient';
import { VenueData, FloorPlan, Room } from '../types/Planner';

// ── API shapes (ans-backend /planning/:project_id/venues, floor-plans, zones) ──
interface ApiVenue {
  id: string;
  name: string;
  address: string | null;
  capacity: number | null;
  capacity_seated_banquet: number | null;
  capacity_seated_conference: number | null;
  capacity_standing: number | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  amenities: string[] | string | null;
  photos?: string[];
  archived: boolean;
}
interface ApiFloorPlan {
  id: string;
  venue_id: string;
  name: string;
  scale: number | string;
  width: number;
  height: number;
  image_url: string | null;
  show_grid: boolean | null;
}
interface ApiZone {
  id: string;
  name: string;
  type: string;
  position_x: number | string;
  position_y: number | string;
  width: number | string;
  height: number | string;
  capacity: number | null;
  assigned_purpose: string | null;
  custom_color: string | null;
  table_shape: string | null;
  seats: number | null;
  group_id: string | null;
  is_head_table: boolean | null;
  locked: boolean | null;
  sort_order: number | null;
}

// Every id is a uuid. A record that has not been saved yet carries a local id
// (the editor makes them) which is not a uuid, so "is it saved" = isUuid(id).
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isUuid = (id: unknown): id is string => typeof id === 'string' && UUID.test(id);

// ── Mapping helpers ────────────────────────────────────────────────────────────

function mapZone(z: ApiZone): Room {
  return {
    id: String(z.id),
    name: z.name,
    type: z.type as Room['type'],
    position: { x: Number(z.position_x), y: Number(z.position_y) },
    width: Number(z.width),
    height: Number(z.height),
    capacity: z.capacity ?? undefined,
    assignedPurpose: z.assigned_purpose ?? undefined,
    customColor: z.custom_color ?? undefined,
    tableShape: (z.table_shape ?? undefined) as Room['tableShape'],
    seats: z.seats ?? undefined,
    groupId: z.group_id ?? undefined,
    isHeadTable: z.is_head_table ?? undefined,
    locked: z.locked ?? undefined,
    sortOrder: z.sort_order ?? undefined,
  };
}

function mapFloorPlan(fp: ApiFloorPlan, rooms: Room[]): FloorPlan {
  return {
    id: String(fp.id),
    venueId: String(fp.venue_id),
    name: fp.name,
    scale: Number(fp.scale) || 10,
    dimensions: { width: fp.width || 1000, height: fp.height || 700 },
    imageUrl: fp.image_url ?? undefined,
    showGrid: fp.show_grid ?? false,
    rooms,
  };
}

function mapVenue(v: ApiVenue, floorPlans: FloorPlan[]): VenueData {
  let amenities: string[] = [];
  if (Array.isArray(v.amenities)) amenities = v.amenities;
  else if (typeof v.amenities === 'string' && v.amenities.startsWith('[')) {
    try { amenities = JSON.parse(v.amenities); } catch { /* ignore */ }
  }
  return {
    id: String(v.id),
    name: v.name,
    address: v.address ?? '',
    capacity: v.capacity ?? 0,
    contactInfo: {
      phone: v.phone ?? undefined,
      email: v.email ?? undefined,
      website: v.website ?? undefined,
    },
    amenities,
    floorPlans,
    seatingPlans: [],
    photos: Array.isArray(v.photos) ? v.photos : undefined,
    archived: v.archived,
    capacitySeatedBanquet: v.capacity_seated_banquet ?? undefined,
    capacitySeatedConference: v.capacity_seated_conference ?? undefined,
    capacityStanding: v.capacity_standing ?? undefined,
  };
}

// ── In-memory cache ────────────────────────────────────────────────────────────

let venuesCache: VenueData[] | null = null;
let fetchPromise: Promise<VenueData[]> | null = null;
// Bumped on every write to venuesCache below. A background fetch captures this
// at the start and checks it again before writing its result: if a write (a
// save, archive, delete...) landed while the fetch was in flight, the fetch's
// now-stale snapshot is discarded instead of clobbering the newer state.
let cacheVersion = 0;

async function fetchFromApi(): Promise<VenueData[]> {
  const startVersion = cacheVersion;
  const prevZonesByFpId = new Map<string, Room[]>();
  for (const v of venuesCache ?? []) for (const fp of v.floorPlans) prevZonesByFpId.set(fp.id, fp.rooms);

  const [venuesRes, fpsRes] = await Promise.all([
    apiGet<{ venues: ApiVenue[] }>(planningPath('/venues?archived=all')),
    apiGet<{ floor_plans: ApiFloorPlan[] }>(planningPath('/floor-plans')),
  ]);

  const apiVenues = venuesRes.venues ?? [];
  const allFps = fpsRes.floor_plans ?? [];

  // A zone fetch failing for one floor plan must not blank out rooms the
  // screen already has: fall back to whatever was cached for that plan
  // (empty for one never loaded before, same as today).
  const zonesByFpId = new Map<string, Room[]>();
  await Promise.all(
    allFps.map(async fp => {
      try {
        const res = await apiGet<{ zones: ApiZone[] }>(planningPath(`/floor-plans/${fp.id}/zones`));
        zonesByFpId.set(fp.id, (res.zones ?? []).map(mapZone));
      } catch {
        zonesByFpId.set(fp.id, prevZonesByFpId.get(fp.id) ?? []);
      }
    })
  );

  const venues = apiVenues.map(v => {
    const venueFps = allFps
      .filter(fp => fp.venue_id === v.id)
      .map(fp => mapFloorPlan(fp, zonesByFpId.get(fp.id) ?? []));
    return mapVenue(v, venueFps);
  });

  // A write that happened while this fetch was in flight already holds the
  // newer truth; don't overwrite it with this now-stale snapshot.
  if (cacheVersion === startVersion) {
    venuesCache = venues;
    cacheVersion++;
    return venuesCache;
  }
  // A conflicting write landed, but it cleared the cache rather than patching
  // it in place (create/delete/archive all null it to force a fresh read) —
  // there is no newer cache to fall back to, so `venues` here is simply
  // stale, not superseded. Ask again instead of resolving with it.
  if (venuesCache) return venuesCache;
  return fetchFromApi();
}

export async function loadVenuesFromApi(): Promise<VenueData[]> {
  if (venuesCache) return venuesCache;
  // A failed load must not be remembered: the next call (Retry) asks again.
  if (!fetchPromise) fetchPromise = fetchFromApi().finally(() => { fetchPromise = null; });
  return fetchPromise;
}

export function getCachedVenues(): VenueData[] {
  return venuesCache ?? [];
}

export function patchVenueCache(updated: VenueData): void {
  if (venuesCache) {
    venuesCache = venuesCache.map(v => v.id === updated.id ? updated : v);
    cacheVersion++;
  }
}

// ── Write operations ───────────────────────────────────────────────────────────

export async function apiCreateVenue(name: string): Promise<{ id: string }> {
  const res = await apiPost<{ id: string }>(planningPath('/venues'), { name });
  venuesCache = null;
  cacheVersion++;
  return { id: res.id };
}

export interface VenueFormData {
  name: string;
  address?: string;
  capacity?: number;
  phone?: string;
  email?: string;
  website?: string;
  amenities?: string[];
  capacitySeatedBanquet?: number;
  capacitySeatedConference?: number;
  capacityStanding?: number;
}

export async function apiCreateVenueFull(data: VenueFormData): Promise<{ id: string }> {
  const res = await apiPost<{ id: string }>(planningPath('/venues'), {
    name: data.name,
    address: data.address || null,
    capacity: data.capacity || null,
    phone: data.phone || null,
    email: data.email || null,
    website: data.website || null,
    amenities: data.amenities ?? [],
    capacity_seated_banquet: data.capacitySeatedBanquet || null,
    capacity_seated_conference: data.capacitySeatedConference || null,
    capacity_standing: data.capacityStanding || null,
  });
  venuesCache = null;
  cacheVersion++;
  return { id: res.id };
}

export async function apiSaveVenueDetails(venue: VenueData): Promise<void> {
  await apiPut(planningPath(`/venues/${venue.id}`), {
    name: venue.name,
    address: venue.address,
    capacity: venue.capacity || null,
    phone: venue.contactInfo.phone ?? null,
    email: venue.contactInfo.email ?? null,
    website: venue.contactInfo.website ?? null,
    amenities: venue.amenities,
    capacity_seated_banquet: venue.capacitySeatedBanquet || null,
    capacity_seated_conference: venue.capacitySeatedConference || null,
    capacity_standing: venue.capacityStanding || null,
  });
  patchVenueCache(venue);
}

// A venue must be archived before it can be deleted — archiving preserves its
// history (past events, floor plans) while marking it out of use, e.g. after
// a contract falls through.
export async function apiSetVenueArchived(venueId: string, archived: boolean): Promise<void> {
  await apiPost(planningPath(`/venues/${venueId}/${archived ? 'archive' : 'restore'}`), {});
  venuesCache = null;
  cacheVersion++;
}

export async function apiDeleteVenue(venueId: string): Promise<void> {
  await apiDelete(planningPath(`/venues/${venueId}`));
  venuesCache = null;
  cacheVersion++;
}

export async function apiSaveFloorPlan(
  venueId: string,
  floorPlan: FloorPlan,
  prevRooms: Room[],
): Promise<FloorPlan> {
  const isNewFp = !isUuid(floorPlan.id);
  let dbFpId: string;

  if (isNewFp) {
    const res = await apiPost<ApiFloorPlan>(planningPath('/floor-plans'), {
      venue_id: venueId,
      name: floorPlan.name,
      scale: floorPlan.scale,
      width: floorPlan.dimensions.width,
      height: floorPlan.dimensions.height,
      image_url: floorPlan.imageUrl ?? null,
      show_grid: floorPlan.showGrid ?? false,
    });
    dbFpId = res.id;
  } else {
    dbFpId = floorPlan.id;
    await apiPut(planningPath(`/floor-plans/${dbFpId}`), {
      name: floorPlan.name,
      scale: floorPlan.scale,
      width: floorPlan.dimensions.width,
      height: floorPlan.dimensions.height,
      image_url: floorPlan.imageUrl ?? null,
      show_grid: floorPlan.showGrid ?? false,
    });
  }

  // Delete zones that were removed since last save
  const prevDbIds = new Set(prevRooms.filter(r => isUuid(r.id)).map(r => r.id));
  const curDbIds = new Set(floorPlan.rooms.filter(r => isUuid(r.id)).map(r => r.id));
  await Promise.all(
    [...prevDbIds].filter(id => !curDbIds.has(id)).map(id =>
      apiDelete(planningPath(`/floor-plans/${dbFpId}/zones/${id}`)),
    ),
  );

  // Upsert current rooms
  const updatedRooms: Room[] = [];
  for (const room of floorPlan.rooms) {
    const payload = {
      name: room.name,
      type: room.type,
      position_x: room.position.x,
      position_y: room.position.y,
      width: room.width,
      height: room.height,
      capacity: room.capacity ?? null,
      assigned_purpose: room.assignedPurpose ?? null,
      custom_color: room.customColor ?? null,
      table_shape: room.tableShape ?? null,
      seats: room.seats ?? null,
      group_id: room.groupId ?? null,
      is_head_table: room.isHeadTable ?? null,
      locked: room.locked ?? null,
      sort_order: room.sortOrder ?? null,
    };
    if (isUuid(room.id)) {
      await apiPut(planningPath(`/floor-plans/${dbFpId}/zones/${room.id}`), payload);
      updatedRooms.push(room);
    } else {
      const res = await apiPost<ApiZone>(planningPath(`/floor-plans/${dbFpId}/zones`), payload);
      updatedRooms.push({ ...room, id: String(res.id) });
    }
  }

  const savedFp: FloorPlan = { ...floorPlan, id: dbFpId, venueId, rooms: updatedRooms };

  if (venuesCache) {
    venuesCache = venuesCache.map(v => {
      if (v.id !== venueId) return v;
      const exists = v.floorPlans.some(fp => fp.id === floorPlan.id || fp.id === savedFp.id);
      return {
        ...v,
        floorPlans: exists
          ? v.floorPlans.map(fp => fp.id === floorPlan.id || fp.id === savedFp.id ? savedFp : fp)
          : [...v.floorPlans, savedFp],
      };
    });
    cacheVersion++;
  }
  return savedFp;
}

export async function apiDeleteFloorPlan(venueId: string, floorPlanId: string): Promise<void> {
  if (isUuid(floorPlanId)) {
    await apiDelete(planningPath(`/floor-plans/${floorPlanId}`));
  }
  if (venuesCache) {
    venuesCache = venuesCache.map(v =>
      v.id === venueId ? { ...v, floorPlans: v.floorPlans.filter(fp => fp.id !== floorPlanId) } : v,
    );
    cacheVersion++;
  }
}

/**
 * Loads again and replaces the cache. Used by the planning sync: a failure
 * rejects and leaves the cached venues as they were (no toast, no empty list).
 */
export async function refreshVenuesFromApi(): Promise<VenueData[]> {
  return fetchFromApi();
}
